import { api } from './client';
import type {
  B2BRow,
  CategoryWiseRow,
  ConsolidatedReport,
  DailyReport,
  PaymentRow,
  ProductWiseRow,
  StoreWiseRow,
  TaxReport,
} from '../types';

export interface RangeQuery {
  from?: string;
  to?: string;
  /** Optional precise ISO timestamps (custom range with a time-of-day). When
   * present they override the plain date bounds server-side. */
  fromDateTime?: string;
  toDateTime?: string;
  storeId?: number | null;
}

export const reportsApi = {
  daily: (storeId: number | null | undefined, date: string) =>
    api
      .get<DailyReport>('/reports/daily', {
        params: { storeId: storeId ?? undefined, date },
      })
      .then((r) => r.data),

  consolidated: (date: string) =>
    api
      .get<ConsolidatedReport>('/reports/consolidated', { params: { date } })
      .then((r) => r.data),

  storeWise: (q: RangeQuery) =>
    api
      .get<StoreWiseRow[]>('/reports/store-wise', {
        params: {
          from: q.from || undefined,
          to: q.to || undefined,
          fromDateTime: q.fromDateTime || undefined,
          toDateTime: q.toDateTime || undefined,
          storeId: q.storeId ?? undefined,
        },
      })
      .then((r) => r.data),

  productWise: (q: RangeQuery) =>
    api
      .get<ProductWiseRow[]>('/reports/product-wise', {
        params: {
          storeId: q.storeId ?? undefined,
          from: q.from || undefined,
          to: q.to || undefined,
          fromDateTime: q.fromDateTime || undefined,
          toDateTime: q.toDateTime || undefined,
        },
      })
      .then((r) => r.data),

  categoryWise: (q: RangeQuery) =>
    api
      .get<CategoryWiseRow[]>('/reports/category-wise', {
        params: {
          storeId: q.storeId ?? undefined,
          from: q.from || undefined,
          to: q.to || undefined,
          fromDateTime: q.fromDateTime || undefined,
          toDateTime: q.toDateTime || undefined,
        },
      })
      .then((r) => r.data),

  payment: (q: RangeQuery) =>
    api
      .get<PaymentRow[]>('/reports/payment', {
        params: {
          storeId: q.storeId ?? undefined,
          from: q.from || undefined,
          to: q.to || undefined,
          fromDateTime: q.fromDateTime || undefined,
          toDateTime: q.toDateTime || undefined,
        },
      })
      .then((r) => r.data),

  tax: (q: RangeQuery) =>
    api
      .get<TaxReport>('/reports/tax', {
        params: {
          storeId: q.storeId ?? undefined,
          from: q.from || undefined,
          to: q.to || undefined,
          fromDateTime: q.fromDateTime || undefined,
          toDateTime: q.toDateTime || undefined,
        },
      })
      .then((r) => r.data),

  // GST (company) bills by bill date, paid or not. Its GSTR-1 CSV comes from
  // /reports/b2b/export (downloaded like the other exports, via downloadCsv).
  b2b: (q: RangeQuery) =>
    api
      .get<B2BRow[]>('/reports/b2b', {
        params: {
          storeId: q.storeId ?? undefined,
          from: q.from || undefined,
          to: q.to || undefined,
          fromDateTime: q.fromDateTime || undefined,
          toDateTime: q.toDateTime || undefined,
        },
      })
      .then((r) => r.data),
};
